// Libs
import { Box, Checkbox, Typography } from "@material-ui/core";
import { useObserver } from "@shoothill/core";
// App
import { Stores, StoresContext } from "Globals/Stores";
import { Row } from "Globals/Styles/LayoutStyles";
import ApplicantViewModel from "Globals/ViewModels/ApplicantViewModel";
// ViewModels
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";
// Views
import { CurrencyInput } from "Globals/Views/CurrencyInput";
import { CustomYesNoRadio } from "Globals/Views/CustomYesNoRadio";
import { CheckboxCheckedSVG } from "Globals/Views/IconSVGs/CheckboxCheckedSVG";
import { CheckboxSVG } from "Globals/Views/IconSVGs/CheckboxSVG";
import React, { useContext, useEffect, useState } from "react";
import { MoneyGifterView } from "Views/Application/FormSections/IncomeSavingsAndOutgoings/MoneyGifterView";
import { CreditCardView } from "./CreditCardView";
import { FinancialCommitmentView } from "./FinancialCommitmentView";
import { OutstandingLoanView } from "./OutstandingLoanView";

interface IProps {
    applicant: ApplicantViewModel;
    applicationFormViewModel: ApplicationFormViewModel;
}

export const IncomeDetailsView: React.FC<IProps> = (props: IProps) => {
    const storesContext = useContext<Stores>(StoresContext);
    const [viewModel] = useState(props.applicant.incomeDetailsViewModel);

    const isPrimaryApplicant = (): boolean => {
        let applicants = props.applicationFormViewModel.applicantViewModels;
        let firstApplicant = applicants.find((a) => a.model.primaryApplicant);
        if (firstApplicant) {
            if (props.applicant.model.id == firstApplicant.model.id) return true;
        }
        return false;
    };

    const getPrimaryApplicant = (): ApplicantViewModel | undefined => {
        let applicants = props.applicationFormViewModel.applicantViewModels;
        let primaryApplicant = applicants.find((a) => a.model && a.model.primaryApplicant);
        return primaryApplicant;
    };

    const showMoneyGifterSection = (): boolean => {
        if (isPrimaryApplicant() == true) return true;
        let primaryApplicant = getPrimaryApplicant();
        if (primaryApplicant != undefined) {
            return primaryApplicant.model.incomeDetailsModel.moneyGifterSameForApplicantTwo == true ? false : true;
        }
        return false;
    };
    const showOutstandingLoanSection = (): boolean => {
        return true;
    };
    const showCreditCardSection = (): boolean => {
        if (isPrimaryApplicant() == true) return true;
        let primaryApplicant = getPrimaryApplicant();
        if (primaryApplicant != undefined) {
            return primaryApplicant.model.incomeDetailsModel.creditCardSameForApplicantTwo == true ? false : true;
        }
        return false;
    };
    const showFinancialCommitmentSection = (): boolean => {
        if (isPrimaryApplicant() == true) return true;
        let primaryApplicant = getPrimaryApplicant();
        if (primaryApplicant != undefined) {
            return primaryApplicant.model.incomeDetailsModel.financialCommitmentSameForApplicantTwo == true ? false : true;
        }
        return false;
    };

    const checkApplicantsIncome = () => {
        let applicants = props.applicationFormViewModel.applicantViewModels;
        let incomeTotal = 0;
        for (let i = 0; i < applicants.length; i++) {
            const applicant = applicants[i];
            let gai = applicant.model.incomeDetailsModel.grossAnnualIncome;
            let raobc = applicant.model.incomeDetailsModel.regularAnnualOvertimeBonusCommission;
            incomeTotal += gai + raobc;
        }
        if (incomeTotal > 80000) {
            for (let i = 0; i < applicants.length; i++) {
                const applicant = applicants[i];
                applicant.incomeDetailsViewModel.applicantIncomeOverAllowedAmmount = true;
            }
        } else {
            for (let i = 0; i < applicants.length; i++) {
                const applicant = applicants[i];
                applicant.incomeDetailsViewModel.applicantIncomeOverAllowedAmmount = false;
            }
        }
    };

    useEffect(() => {
        checkApplicantsIncome();
    }, []);

    return useObserver(() => (
        <div onMouseDown={() => viewModel.restartTimer()} onKeyUp={() => viewModel.restartTimer()}>
            {viewModel.DevTool_ShowCanProceed && <h5 style={{ color: "red" }}>Can Proceed: {viewModel.canProceed ? "Yes" : "No"}</h5>}
            {/* Gross annual and regular oertime */}
            <Row>
                <CurrencyInput
                    onChange={() => {
                        checkApplicantsIncome();
                        viewModel.handleInputChange();
                    }}
                    viewModel={viewModel}
                    label={"Gross annual income"}
                    smallLabel={"(Excluding overtime / bonus / comission)"}
                    placeholder={"Enter gross annual income"}
                    fieldName="grossAnnualIncome"
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
                <CurrencyInput
                    onChange={() => {
                        checkApplicantsIncome();
                        viewModel.handleInputChange();
                    }}
                    viewModel={viewModel}
                    label={"Regular annual overtime / bonus / commission"}
                    placeholder={"regular overtime/bonus/commission"}
                    smallLabel={"(Before deductions)"}
                    fieldName="regularAnnualOvertimeBonusCommission"
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row>
            <Row>
                <CustomYesNoRadio
                    fieldName={"hasAdditionalMonthlyHouseholdIncome"}
                    label="Do you have any additional monthly household income?"
                    value={viewModel.model.hasAdditionalMonthlyHouseholdIncome}
                    onChange={viewModel.handleRadioChange}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                    error={viewModel.getError("hasAdditionalMonthlyHouseholdIncome")}
                />
            </Row>
            {viewModel.model.hasAdditionalMonthlyHouseholdIncome && (
                <Box style={{ backgroundColor: "#F0F0F0", padding: "15px" }}>
                    <Row>
                        <CurrencyInput
                            onChange={() => viewModel.handleInputChange()}
                            viewModel={viewModel}
                            label={"Working tax credits"}
                            placeholder={"Enter your working tax credits"}
                            fieldName={"workingTaxCredit"}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                        />
                        <CurrencyInput
                            onChange={() => viewModel.handleInputChange()}
                            viewModel={viewModel}
                            label={"Child tax credits"}
                            placeholder={"Enter your child tax credits"}
                            fieldName={"childTaxCredit"}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                        />
                        <CurrencyInput
                            onChange={() => viewModel.handleInputChange()}
                            viewModel={viewModel}
                            label={"Disability Allowance"}
                            placeholder={"Enter your disability allowance"}
                            fieldName={"disabilityAllowance"}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                        />
                    </Row>
                    <Row>
                        <CurrencyInput
                            onChange={() => viewModel.handleInputChange()}
                            viewModel={viewModel}
                            label={"Guaranteed maintenance income"}
                            placeholder={"Enter your guaranteed maintenance income"}
                            fieldName={"guaranteedMaintenanceIncome"}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                        />
                        <CurrencyInput
                            onChange={() => viewModel.handleInputChange()}
                            viewModel={viewModel}
                            label={"Other income"}
                            placeholder={"Enter your other income"}
                            fieldName={"otherIncome"}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                        />
                    </Row>
                </Box>
            )}
            <Row>
                <CurrencyInput
                    onChange={() => viewModel.handleInputChange()}
                    viewModel={viewModel}
                    label={"Total savings"}
                    placeholder={"Enter your total savings"}
                    fieldName={"totalSavings"}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row>
            {/* Hide this section if primary applicant has marked their section as he same for applicant two */}
            {showMoneyGifterSection() && (
                <>
                    <Row>
                        <CustomYesNoRadio
                            label="Are you being gifted money towards your purchase?"
                            fieldName={"claimsGiftedMoney"}
                            value={viewModel.model.claimsGiftedMoney}
                            onChange={viewModel.handleRadioChange}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                            error={viewModel.getError("claimsGiftedMoney")}
                        />
                    </Row>
                    {/* Money gifter section */}
                    {viewModel.model.claimsGiftedMoney && (
                        <Box style={{ backgroundColor: "#F0F0F0", padding: "15px" }}>
                            <MoneyGifterView applicationFormViewModel={props.applicationFormViewModel} incomeDetailsViewModel={viewModel} isPrimary={isPrimaryApplicant()} />
                        </Box>
                    )}
                </>
            )}

            <Typography variant="h6" style={{ marginTop: "20px" }}>
                Outgoings
            </Typography>
            {/* <Row>
                <CurrencyInput
                    onChange={() => viewModel.handleInputChange()}
                    viewModel={viewModel}
                    label={"Current rent"}
                    placeholder={"Enter your current rent"}
                    fieldName={"currentRent"}
                    disabled={props.applicationFormViewModel.model.isSubmitted}
                />
            </Row> */}
            {/* Hide this section if primary applicant has marked their section as he same for applicant two */}
            {showOutstandingLoanSection() && (
                <>
                    <Row>
                        <CustomYesNoRadio
                            label="Do you have any outstanding loans?"
                            smallLabel="(e.g. hire purchase, store card payments and student loans)"
                            fieldName={"hasOutstandingLoans"}
                            value={viewModel.model.hasOutstandingLoans}
                            onChange={viewModel.handleRadioChange}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                            error={viewModel.getError("hasOutstandingLoans")}
                        />
                    </Row>
                    {viewModel.model.hasOutstandingLoans && (
                        <Box style={{ backgroundColor: "#F0F0F0", padding: "15px" }}>
                            <OutstandingLoanView applicationFormViewModel={props.applicationFormViewModel} incomeDetailsViewModel={viewModel} isPrimary={isPrimaryApplicant()} />
                        </Box>
                    )}
                </>
            )}
            {/* Hide this section if primary applicant has marked their section as he same for applicant two */}
            {showCreditCardSection() && (
                <>
                    <Row>
                        <CustomYesNoRadio
                            label="Do you currently hold any credit cards?"
                            fieldName={"hasCreditCards"}
                            value={viewModel.model.hasCreditCards}
                            onChange={viewModel.handleRadioChange}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                            error={viewModel.getError("hasCreditCards")}
                        />
                    </Row>
                    {viewModel.model.hasCreditCards && (
                        <Box style={{ backgroundColor: "#F0F0F0", padding: "15px" }}>
                            <CreditCardView applicationFormViewModel={props.applicationFormViewModel} incomeDetailsViewModel={viewModel} isPrimary={isPrimaryApplicant()} />
                        </Box>
                    )}
                </>
            )}
            {/* Hide this section if primary applicant has marked their section as he same for applicant two */}
            {showFinancialCommitmentSection() && (
                <>
                    <Row>
                        <CustomYesNoRadio
                            label="Do you have any other regular monthly financial commitments?"
                            fieldName={"hasOtherRegularMonthlyFinancialCommitments"}
                            value={viewModel.model.hasOtherRegularMonthlyFinancialCommitments}
                            onChange={viewModel.handleRadioChange}
                            disabled={props.applicationFormViewModel.model.isSubmitted}
                            error={viewModel.getError("hasOtherRegularMonthlyFinancialCommitments")}
                        />
                    </Row>
                    {viewModel.model.hasOtherRegularMonthlyFinancialCommitments && (
                        <Box style={{ backgroundColor: "#F0F0F0", padding: "15px" }}>
                            <FinancialCommitmentView
                                applicationFormViewModel={props.applicationFormViewModel}
                                incomeDetailsViewModel={viewModel}
                                isPrimary={isPrimaryApplicant()}
                            />
                        </Box>
                    )}
                </>
            )}
        </div>
    ));
};
